import { SelfUserProfile } from 'types/user';
import { organizationUrls, communityUrls } from 'urls';
import { PARTNER_OFFER_TEMPLATE_VARIABLES } from 'pages/Backoffice/PartnerOffers/PartnerOfferForm';

// Helper function to determine if URL is internal (same domain)
export const isInternalUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.hostname === window.location.hostname;
  } catch {
    return false;
  }
};

// Helper function to replace user variables in URL
export const replaceUserVariables = (url: string, userProfile?: SelfUserProfile): string => {
  if (!userProfile) return url;

  const locationString = userProfile.location
    ? userProfile.location.description ||
      `${userProfile.location.place || ''}${userProfile.location.country ? `, ${userProfile.location.country}` : ''}`
    : `${userProfile.city || ''}${userProfile.country ? `, ${userProfile.country}` : ''}`;

  return url
    .replace(
      new RegExp(PARTNER_OFFER_TEMPLATE_VARIABLES.NAME.replace(/[{}]/g, '\\$&'), 'g'),
      encodeURIComponent(userProfile.name || ''),
    )
    .replace(
      new RegExp(PARTNER_OFFER_TEMPLATE_VARIABLES.POSITION.replace(/[{}]/g, '\\$&'), 'g'),
      encodeURIComponent(userProfile.position || ''),
    )
    .replace(
      new RegExp(PARTNER_OFFER_TEMPLATE_VARIABLES.EMPLOYER.replace(/[{}]/g, '\\$&'), 'g'),
      encodeURIComponent(userProfile.employer || ''),
    )
    .replace(
      new RegExp(PARTNER_OFFER_TEMPLATE_VARIABLES.PHONE.replace(/[{}]/g, '\\$&'), 'g'),
      encodeURIComponent(userProfile.phone || ''),
    )
    .replace(
      new RegExp(PARTNER_OFFER_TEMPLATE_VARIABLES.EMAIL.replace(/[{}]/g, '\\$&'), 'g'),
      encodeURIComponent(userProfile.email || ''),
    )
    .replace(
      new RegExp(PARTNER_OFFER_TEMPLATE_VARIABLES.LOCATION.replace(/[{}]/g, '\\$&'), 'g'),
      encodeURIComponent(locationString.trim()),
    );
};

interface LinkInfoParams {
  linkUrl?: string;
  organizationSlug?: string;
  communitySlug?: string;
  companyId?: number;
  type?: string;
}

export interface LinkInfo {
  href: string;
  isInternal: boolean;
  isOrganization?: boolean;
  isCompany?: boolean;
}

// Helper function to get the correct href based on link type
export const getLinkInfo = (params: LinkInfoParams, userProfile?: SelfUserProfile): LinkInfo => {
  if (!params.linkUrl && params.type === 'Organization' && params.organizationSlug) {
    return {
      href: organizationUrls.view(params.organizationSlug),
      isInternal: true,
      isOrganization: true,
    };
  } else if (!params.linkUrl && params.type === 'Community' && params.communitySlug) {
    return {
      href: communityUrls.overview(params.communitySlug),
      isInternal: true,
      isOrganization: true,
    };
  } else if (!params.linkUrl && params.type === 'Company' && params.companyId) {
    return {
      href: '', // No navigation for company type
      isInternal: false,
      isCompany: true,
    };
  } else if (!params.linkUrl && params.organizationSlug) {
    // For dashboard messages that might not have a type but have organizationSlug
    return {
      href: organizationUrls.view(params.organizationSlug),
      isInternal: true,
    };
  } else if (!params.linkUrl && params.communitySlug) {
    // For dashboard messages that might not have a type but have communitySlug
    return {
      href: communityUrls.overview(params.communitySlug),
      isInternal: true,
    };
  } else {
    const processedUrl = params.linkUrl ? replaceUserVariables(params.linkUrl, userProfile) : '';
    return {
      href: processedUrl,
      isInternal: isInternalUrl(processedUrl),
      isOrganization: false,
    };
  }
};
